import React from 'react';
import { useTranslation } from 'react-i18next';

interface CategoryBreakdownCardProps {
  categoryBreakdown: Array<{
    code: string;
    name: string;
    score: number;
    color: string;
  }>;
  selectedCategories: Set<string>;
}

export const CategoryBreakdownCard: React.FC<CategoryBreakdownCardProps & { language?: string }> = ({
  categoryBreakdown,
  selectedCategories,
}) => {
  const { t } = useTranslation();

  const getCategoryIcon = (categoryCode: string) => {
    switch (categoryCode) {
      case 'MILITARY':
        return '🎯';
      case 'CYBER':
        return '🔐';
      case 'DIPLOMATIC':
        return '🤝';
      case 'ECONOMIC':
        return '💰';
      case 'INFORMATION_WARFARE':
        return '📰';
      default:
        return '⚠️';
    }
  };

  const filtered = categoryBreakdown.filter((c) => selectedCategories.has(c.code));

  const levelFor = (score: number) => {
    if (score >= 8) return { key: 'critical', className: 'text-red-600' };
    if (score >= 6) return { key: 'high', className: 'text-orange-600' };
    if (score >= 4) return { key: 'medium', className: 'text-yellow-600' };
    return { key: 'low', className: 'text-green-600' };
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4">
      <div className="mb-3">
        <h3 className="text-lg font-bold text-gray-900">{t('public.categories.title')}</h3>
      </div>

      <div className="space-y-2">
        {filtered.map((category) => {
          const level = levelFor(category.score);
          const label = t(`public.categoryNames.${category.code}`);
          return (
            <div
              key={category.code}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200"
            >
              <div className="text-xl shrink-0">{getCategoryIcon(category.code)}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="truncate font-medium text-gray-900">{label}</div>
                  <div className="flex items-baseline gap-2 shrink-0">
                    <span className="text-xl font-bold" style={{ color: category.color }}>
                      {category.score}
                    </span>
                    <span className={`text-xs font-medium ${level.className}`}>
                      {t(`public.riskLevels.${level.key}`)}
                    </span>
                  </div>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
                  <div
                    className="h-1.5 rounded-full transition-all"
                    style={{ width: `${(category.score / 10) * 100}%`, backgroundColor: category.color }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
